import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import queueService from '../../services/queueService';
import {
  QUEUE_STAGES, STATUS_LABELS, formatWait, issuePayload, nextStageOf, stageLabel, ticketActions,
} from '../../utils/queueLogic';
import './QueueDeskPage.css';

const POLL_MS = 5000;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const rememberedCounter = (key) => {
  try { return Number(window.localStorage.getItem(key)) || 0; } catch (e) { return 0; }
};
const rememberCounter = (key, id) => {
  try { window.localStorage.setItem(key, String(id || '')); } catch (e) { /* storage may be blocked; the choice is just not remembered */ }
};

/** Counters of the centre: add one, switch one off. Only an Administrator or Centre Admin gets here. */
const CountersModal = ({ onClose, onChanged }) => {
  const [counters, setCounters] = useState([]);
  const [name, setName] = useState('');
  const [stage, setStage] = useState('REGISTRATION');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await queueService.counters({ includeInactive: true });
      if (res.success) setCounters(res.data || []);
      else setError(res.message || 'Could not load the counters.');
    } catch (err) { setError(err.response?.data?.message || 'Could not load the counters.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      const res = await queueService.saveCounter(payload);
      if (!res.success) { setError(res.message || 'Could not save the counter.'); return; }
      setName('');
      await load();
      onChanged();
    } catch (err) { setError(err.response?.data?.message || 'Could not save the counter.'); } finally { setSaving(false); }
  };

  return (
    <div className="que-modal-overlay" role="dialog" aria-modal="true" aria-label="Counters" onClick={onClose}>
      <div className="que-modal" onClick={(e) => e.stopPropagation()}>
        <div className="que-modal-head"><h2>Counters</h2><button type="button" className="que-icon-btn" aria-label="Close" onClick={onClose}>×</button></div>
        <div className="que-modal-body">
          <div className="que-form-row">
            <input className="que-input" placeholder="Counter name, e.g. Desk 1" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} aria-label="Counter name" />
            <select className="que-input" value={stage} onChange={(e) => setStage(e.target.value)} aria-label="Stage">
              {QUEUE_STAGES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
            <button type="button" className="que-btn que-btn-primary" disabled={saving || !name.trim()} onClick={() => save({ counterName: name.trim(), stage })}>Add counter</button>
          </div>
          {error && <p className="que-error" role="alert">{error}</p>}
          {counters.length === 0 ? <p className="que-muted">No counters yet.</p> : (
            <table className="que-table">
              <thead><tr><th>Counter</th><th>Stage</th><th>Status</th><th /></tr></thead>
              <tbody>
                {counters.map((c) => (
                  <tr key={c.counterId}>
                    <td>{c.counterName}</td>
                    <td>{stageLabel(c.stage)}</td>
                    <td>{c.isActive ? 'On' : 'Off'}</td>
                    <td className="que-right">
                      <button type="button" className="que-btn que-btn-sm" disabled={saving}
                        onClick={() => save({ counterId: c.counterId, counterName: c.counterName, stage: c.stage, isActive: !c.isActive })}>
                        {c.isActive ? 'Switch off' : 'Switch on'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/** Tokens issued, done, skipped and the waiting time per stage, and the busy hours. */
const ReportPanel = () => {
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    queueService.report(from, to)
      .then((res) => { if (!cancelled) { if (res.success) setReport(res.data); else setError(res.message || 'Could not load the report.'); } })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'Could not load the report.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const maxHour = Math.max(1, ...(report?.hours || []).map((h) => h.issued));
  return (
    <section className="que-card" aria-label="Queue report">
      <div className="que-card-head">
        <h2>Report</h2>
        <div className="que-form-row">
          <label className="que-inline-label">From <input type="date" className="que-input que-input-sm" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="que-inline-label">To <input type="date" className="que-input que-input-sm" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
        </div>
      </div>
      {loading && <p className="que-muted">Loading…</p>}
      {error && <p className="que-error" role="alert">{error}</p>}
      {report && report.stages.length === 0 && !loading && <p className="que-muted">No tokens in this period.</p>}
      {report && report.stages.length > 0 && (
        <>
          <div className="que-table-wrap">
            <table className="que-table">
              <thead><tr><th>Stage</th><th>Issued</th><th>Done</th><th>Skipped</th><th>Still open</th><th>Avg wait</th><th>Longest wait</th><th>Avg service</th></tr></thead>
              <tbody>
                {report.stages.map((s) => (
                  <tr key={s.stage}>
                    <td>{stageLabel(s.stage)}</td><td>{s.issued}</td><td>{s.done}</td><td>{s.skipped}</td><td>{s.stillOpen}</td>
                    <td>{formatWait(s.avgWaitMinutes)}</td><td>{formatWait(s.maxWaitMinutes)}</td><td>{formatWait(s.avgServiceMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="que-sub">Tokens issued by hour</h3>
          <div className="que-bars" role="img" aria-label="Tokens issued by hour">
            {report.hours.map((h) => (
              <div key={h.hour} className="que-bar-col" title={`${h.hour}:00 - ${h.issued} tokens`}>
                <div className="que-bar" style={{ height: `${Math.max(6, (h.issued / maxHour) * 100)}%` }} />
                <span>{h.hour}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export const QueueDeskPage = () => {
  const { user, can } = useAuth();
  const isAdmin = user?.roleName?.toUpperCase() === 'ADMINISTRATOR' || user?.roleId === 1;
  const perms = {
    issue: can('QUEUE', 'CREATE'),
    act: can('QUEUE', 'UPDATE'),
    settings: can('QUEUE', 'UPDATE') && (isAdmin || user?.roleName?.toUpperCase() === 'CENTRE ADMIN'),
  };
  const storageKey = `lis.queue.counter.${user?.employeeId || user?.userId || 'me'}`;

  const [counters, setCounters] = useState([]);
  const [counterId, setCounterId] = useState(() => rememberedCounter(storageKey));
  const [tickets, setTickets] = useState([]);
  const [stageTab, setStageTab] = useState('REGISTRATION');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [showReport, setShowReport] = useState(false);
  const [showCounters, setShowCounters] = useState(false);
  const [issue, setIssue] = useState({ stage: 'REGISTRATION', priority: false, name: '', mobile: '' });
  const [sendTo, setSendTo] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  const loadCounters = useCallback(async () => {
    try {
      const res = await queueService.counters();
      if (res.success) setCounters(res.data || []);
    } catch (err) { setError(err.response?.data?.message || 'Could not load the counters.'); }
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      const res = await queueService.list({});
      if (res.success) { setTickets(res.data || []); setError(''); } else setError(res.message || 'Could not load the queue.');
    } catch (err) { setError(err.response?.data?.message || 'Could not load the queue.'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCounters(); loadTickets(); }, [loadCounters, loadTickets]);

  // Other desks change the queue all day: read it again every few seconds while the tab is visible.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) loadTickets(); }, POLL_MS);
    return () => clearInterval(id);
  }, [loadTickets]);

  const myCounter = useMemo(() => counters.find((c) => c.counterId === counterId) || null, [counters, counterId]);
  useEffect(() => { if (myCounter) setStageTab(myCounter.stage); }, [myCounter]);
  // A remembered counter that was switched off or belongs to no centre list any more is dropped.
  useEffect(() => {
    if (counters.length > 0 && counterId && !counters.some((c) => c.counterId === counterId)) setCounterId(0);
  }, [counters, counterId]);

  const current = useMemo(
    () => tickets.find((t) => t.counterId === counterId && (t.status === 'CALLED' || t.status === 'SERVING')) || null,
    [tickets, counterId],
  );
  const inStage = useMemo(() => tickets.filter((t) => t.stage === stageTab), [tickets, stageTab]);
  const waiting = useMemo(
    () => inStage.filter((t) => t.status === 'WAITING').sort((a, b) => (Number(b.priority) - Number(a.priority)) || (a.tokenNo - b.tokenNo)),
    [inStage],
  );
  const skipped = useMemo(() => inStage.filter((t) => t.status === 'SKIPPED'), [inStage]);
  const called = useMemo(() => inStage.filter((t) => t.status === 'CALLED' || t.status === 'SERVING'), [inStage]);
  const openCount = (code) => tickets.filter((t) => t.stage === code && t.status === 'WAITING').length;

  const chooseCounter = (id) => { setCounterId(id); rememberCounter(storageKey, id); };

  const act = async (fn, okMessage) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      const res = await fn();
      if (res.success) { if (okMessage || res.message) showToast(okMessage || res.message); } else showToast(res.message || 'The action failed.', 'error');
    } catch (err) {
      showToast(err.response?.data?.message || 'The action failed.', 'error');
    } finally {
      busyRef.current = false; setBusy(false);
      loadTickets();
    }
  };

  const callNext = () => act(async () => {
    const res = await queueService.callNext(counterId);
    if (res.success && !res.data) return { success: true, message: 'No one is waiting.' };
    return res;
  });
  const change = (t, action, ok) => act(() => queueService.change(t.ticketId, action, action === 'recall' && t.status === 'SKIPPED' ? counterId : undefined), ok);
  const transfer = (t) => act(() => queueService.transfer(t.ticketId, sendTo || nextStageOf(t.stage)), `Sent to ${stageLabel(sendTo || nextStageOf(t.stage))}.`);

  const submitIssue = async (e) => {
    e.preventDefault();
    await act(async () => {
      const res = await queueService.issue(issuePayload(issue));
      if (res.success) {
        setIssue((f) => ({ ...f, name: '', mobile: '', priority: false }));
        return { ...res, message: `Token ${res.data.tokenLabel} issued.` };
      }
      return res;
    });
  };

  const nextForCurrent = current ? nextStageOf(current.stage) : null;
  const actions = current ? ticketActions(current.status) : {};

  return (
    <div className="que-page">
      {toast && <div className={`que-toast ${toast.type === 'error' ? 'error' : ''}`} role="alert">{toast.message}</div>}

      <div className="que-header">
        <div>
          <h1>Queue desk</h1>
          <p className="que-subtitle">Issue tokens, call the next patient at your counter and send them on to the next stage.</p>
        </div>
        <div className="que-header-actions">
          <Link to="/queue/display" className="que-btn" target="_blank" rel="noopener noreferrer">Open waiting-room display</Link>
          <button type="button" className="que-btn" onClick={() => setShowReport((v) => !v)} aria-pressed={showReport}>Report</button>
          {perms.settings && <button type="button" className="que-btn" onClick={() => setShowCounters(true)}>Counters</button>}
        </div>
      </div>

      {error && <p className="que-error" role="alert">{error}</p>}

      <div className="que-grid">
        <section className="que-card" aria-label="My counter">
          <h2>My counter</h2>
          <label className="que-label" htmlFor="que-counter">Counter</label>
          <select id="que-counter" className="que-input" value={counterId || ''} onChange={(e) => chooseCounter(Number(e.target.value) || 0)}>
            <option value="">Choose your counter…</option>
            {QUEUE_STAGES.map((s) => (
              <optgroup key={s.code} label={s.label}>
                {counters.filter((c) => c.stage === s.code).map((c) => <option key={c.counterId} value={c.counterId}>{c.counterName}</option>)}
              </optgroup>
            ))}
          </select>
          {counters.length === 0 && !loading && (
            <p className="que-hint">No counters are set up for this centre yet.{perms.settings ? ' Use “Counters” to add them.' : ' Ask an Administrator or Centre Admin.'}</p>
          )}

          {myCounter && !current && (
            <div className="que-idle">
              <p className="que-muted">{myCounter.counterName} is free. {openCount(myCounter.stage)} waiting at {stageLabel(myCounter.stage)}.</p>
              <button type="button" className="que-btn que-btn-primary que-btn-lg" disabled={busy || !perms.act} onClick={callNext}>Call next</button>
            </div>
          )}

          {myCounter && current && (
            <div className="que-now" aria-live="polite">
              <div className="que-now-label">{current.status === 'SERVING' ? 'Serving' : 'Called'}</div>
              <div className="que-now-token">{current.tokenLabel}</div>
              <div className="que-now-patient">{current.pName || 'No name given'}{current.mobile ? ` · ${current.mobile}` : ''}</div>
              <div className="que-muted">Waited {formatWait(current.waitingMinutes)}{current.priority ? ' · Priority' : ''}{current.labNo ? ` · Lab no ${current.labNo}` : ''}</div>
              <div className="que-actions">
                {actions.start && <button type="button" className="que-btn que-btn-primary" disabled={busy || !perms.act} onClick={() => change(current, 'start', 'Serving started.')}>Start</button>}
                {actions.complete && <button type="button" className="que-btn que-btn-primary" disabled={busy || !perms.act} onClick={() => change(current, 'complete', 'Token completed.')}>Done</button>}
                {actions.recall && <button type="button" className="que-btn" disabled={busy || !perms.act} onClick={() => change(current, 'recall', 'Called again.')}>Call again</button>}
                {actions.skip && <button type="button" className="que-btn" disabled={busy || !perms.act} onClick={() => change(current, 'skip', 'Token skipped.')}>Skip</button>}
                {actions.requeue && <button type="button" className="que-btn" disabled={busy || !perms.act} onClick={() => change(current, 'requeue', 'Returned to the line.')}>Back to line</button>}
              </div>
              {actions.transfer && (
                <div className="que-form-row">
                  <select className="que-input que-input-sm" aria-label="Send to stage" value={sendTo || nextForCurrent || ''} onChange={(e) => setSendTo(e.target.value)}>
                    {QUEUE_STAGES.filter((s) => s.code !== current.stage).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                  </select>
                  <button type="button" className="que-btn" disabled={busy || !perms.act} onClick={() => transfer(current)}>Finish and send on</button>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="que-card" aria-label="Issue token">
          <h2>Issue token</h2>
          {!perms.issue ? <p className="que-muted">You do not have permission to issue tokens.</p> : (
            <form onSubmit={submitIssue}>
              <label className="que-label" htmlFor="que-stage">Stage</label>
              <select id="que-stage" className="que-input" value={issue.stage} onChange={(e) => setIssue({ ...issue, stage: e.target.value })}>
                {QUEUE_STAGES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
              </select>
              <label className="que-label" htmlFor="que-name">Patient name (optional)</label>
              <input id="que-name" className="que-input" maxLength={150} value={issue.name} onChange={(e) => setIssue({ ...issue, name: e.target.value })} />
              <label className="que-label" htmlFor="que-mobile">Mobile (optional)</label>
              <input id="que-mobile" className="que-input" inputMode="numeric" maxLength={15} value={issue.mobile} onChange={(e) => setIssue({ ...issue, mobile: e.target.value })} />
              <label className="que-inline-label que-check"><input type="checkbox" checked={issue.priority} onChange={(e) => setIssue({ ...issue, priority: e.target.checked })} /> Priority (senior citizen, urgent, disabled)</label>
              <button type="submit" className="que-btn que-btn-primary" disabled={busy}>Issue token</button>
            </form>
          )}
        </section>
      </div>

      <section className="que-card" aria-label="Queue">
        <div className="que-tabs" role="tablist">
          {QUEUE_STAGES.map((s) => (
            <button key={s.code} type="button" role="tab" aria-selected={stageTab === s.code} className={`que-tab ${stageTab === s.code ? 'active' : ''}`} onClick={() => setStageTab(s.code)}>
              {s.label} <span className="que-count">{openCount(s.code)}</span>
            </button>
          ))}
        </div>

        {loading ? <p className="que-muted">Loading…</p> : (
          <>
            {called.length > 0 && (
              <p className="que-muted">At counters now: {called.map((t) => `${t.tokenLabel} → ${t.counterName || '—'}`).join(' · ')}</p>
            )}
            {waiting.length === 0 ? <p className="que-muted">No one is waiting at {stageLabel(stageTab)}.</p> : (
              <div className="que-table-wrap">
                <table className="que-table">
                  <thead><tr><th>Token</th><th>Patient</th><th>Mobile</th><th>Waiting</th><th /></tr></thead>
                  <tbody>
                    {waiting.map((t) => (
                      <tr key={t.ticketId}>
                        <td><strong>{t.tokenLabel}</strong>{t.priority && <span className="que-badge" title="Priority">★ Priority</span>}</td>
                        <td>{t.pName || '—'}</td>
                        <td>{t.mobile || '—'}</td>
                        <td>{formatWait(t.waitingMinutes)}</td>
                        <td />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {skipped.length > 0 && (
              <>
                <h3 className="que-sub">Skipped</h3>
                <div className="que-table-wrap">
                  <table className="que-table">
                    <tbody>
                      {skipped.map((t) => (
                        <tr key={t.ticketId}>
                          <td><strong>{t.tokenLabel}</strong></td>
                          <td>{t.pName || '—'}</td>
                          <td>{STATUS_LABELS[t.status]}</td>
                          <td className="que-right">
                            <button type="button" className="que-btn que-btn-sm" disabled={busy || !perms.act || !myCounter || myCounter.stage !== t.stage || !!current}
                              title={!myCounter ? 'Choose your counter first' : (current ? 'Finish the current token first' : 'Call this patient again at your counter')}
                              onClick={() => change(t, 'recall', 'Called again.')}>Call again</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </section>

      {showReport && <ReportPanel />}
      {showCounters && <CountersModal onClose={() => setShowCounters(false)} onChanged={loadCounters} />}
    </div>
  );
};

export default QueueDeskPage;
